<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">
            Manage Processing Methods
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <div class="modal-card-body">

          <button 
            class="button is-small mb-3" 
            @click="managingTypes = true">
            Manage Processing Method Types
          </button>

          <article 
            v-if="!loading && processingMethods.length == 0" 
            class="message is-info is-fullwidth mt-3">
            <div class="message-body">
              There are no processing methods.
            </div>
          </article>

          <v-fade-transition mode="out-in">
            <div 
              v-if="loading" 
              class="is-relative">
              <v-spin-loader :value="true">
                <div class="py-6"></div>
              </v-spin-loader>
            </div>

            <table 
              v-else
              class="table is-fullwidth is-hoverable">

              <thead>
                <tr>
                  <th 
                    v-for="(header, headerIndex) in headers" 
                    :key="headerIndex">
                    {{ header.label }}
                  </th>
                  <th></th>
                </tr>
              </thead>

              <tfoot>
                <tr>
                  <td :colspan="headers.length + 1">
                    <div class="centered">
                      <button
                        v-if="!adding"
                        @click="adding = true" 
                        class="button is-primary is-small">
                        Add a new Processing Method
                      </button>
                    </div>
                  </td>
                </tr>
              </tfoot>

              <tbody>
                <tr 
                  class="v-centered"
                  v-for="processingMethod in processingMethods" 
                  :key="processingMethod.id">

                  <template v-if="editingId != processingMethod.id">
                    <td>
                      {{ processingMethod.processing_name }}
                    </td>
                    <td>
                      {{ processingMethod.processing_type.processing_type_name }}
                    </td>
                    <td>
                      {{ processingMethod.unit ? processingMethod.unit.unit_display_name : '' }}
                    </td>
                    <td>
                      {{ processingMethod.unit_price | currency(2) }}
                    </td>
                    <td>
                      {{ processingMethod.waste_pct | percentage }}
                    </td>
                  </template>

                  <template v-else>
                    <td>
                      <v-text-field 
                        required
                        placeholder="Enter Processing Method Name"
                        v-model="processingMethod.processing_name">
                      </v-text-field>
                    </td>
                    <td>
                      <v-select
                        required
                        :options="processingTypes"
                        item-text="processing_type_name"
                        item-value="id"
                        v-model="processingMethod.processing_type_id"
                      />
                    </td>
                    <td>
                      <v-select
                        required
                        :options="units"
                        item-text="unit_display_name"
                        item-value="id"
                        v-model="processingMethod.unit_id"
                      />
                    </td>
                    <td>
                      <v-text-field 
                        required
                        type="number"
                        placeholder="Enter Unit Price"
                        v-model="processingMethod.unit_price">
                      </v-text-field>
                    </td>
                    <td>
                      <v-text-field 
                        placeholder="Enter waste percentage"
                        type="number"
                        v-model="processingMethod.waste_pct">
                      </v-text-field>
                    </td>
                  </template>
                  <td>
                    <div class="buttons buttons-right">
                      <template v-if="editingId != processingMethod.id">
                        <button
                          @click="editingId = processingMethod.id" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-edit has-text-grey"></i>
                          </span>
                        </button>
                        <button
                          @click="launchConfirmDelete(processingMethod.id)" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-trash has-text-grey"></i>
                          </span>
                        </button>
                      </template>
                      <template v-else>
                        <button
                          @click="update(processingMethod)" 
                          class="button is-primary is-small">
                          <span class="icon">
                            <i class="fas fa-save"></i>
                          </span>
                        </button>
                        <button
                          @click="editingId = null" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-times has-text-grey"></i>
                          </span>
                        </button>
                      </template>
                    </div>
                  </td>
                </tr>

                <tr class="v-centered" v-if="adding">
                  <td>
                    <v-text-field 
                      required
                      placeholder="Enter Processing Method Name"
                      v-model="processing.processing_name">
                    </v-text-field>
                  </td>
                  <td>
                    <v-select
                      required
                      :options="processingTypes"
                      item-text="processing_type_name"
                      item-value="id"
                      v-model="processing.processing_type_id"
                    />
                  </td>
                  <td>
                    <v-select
                      required
                      :options="units"
                      item-text="unit_display_name"
                      item-value="id"
                      v-model="processing.unit_id"
                    />
                  </td>
                  <td>
                    <v-text-field
                      required 
                      placeholder="Enter Processing Method Unit Price"
                      type="number"
                      v-model="processing.unit_price"
                    />
                  </td>
                  <td>
                    <v-text-field 
                      placeholder="Enter Processing Method Waste Percentage"
                      type="number"
                      v-model="processing.waste_pct"
                    />
                  </td>
                  <td>
                    <div
                      class="buttons buttons-right">
                      <button
                        @click="add" 
                        class="button is-primary is-small">
                        <span class="icon">
                          <i class="fas fa-save"></i>
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-fade-transition>

        </div>

        <v-confirm-modal 
          v-model="confirmDelete.state"
          :title="confirmDelete.title"
          :message="confirmDelete.message"
          :type="confirmDelete.type"
          @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
          @cancel="confirmDelete.state = false"/>

        <manage-processing-method-types-modal v-model="managingTypes"/>

      </div>
    </div>
  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import VSpinLoader from '@/components/base/VSpinLoader'
import VConfirmModal from '@/components/base/VConfirmModal'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import ManageProcessingMethodTypesModal from '@/components/estimations/ManageProcessingMethodTypesModal'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    }
  },
  components: { 
    VTextField, VSelect, VSpinLoader, VFadeTransition,
    VConfirmModal, ManageProcessingMethodTypesModal 
  },
  data() {
    return {
      headers: [
        { label: 'Name' },
        { label: 'Type' },
        { label: 'Unit Type' },
        { label: 'Unit Price' },
        { label: 'Waste %' } 
      ],
      processing: {
        processing_name: null,
        processing_type_id: null,
        unit_id: null,
        unit_price: null,
        waste_pct: null
      },
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this processing method?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      valid: false,
      adding: false,
      editingId: null,
      managingTypes: false
    }
  },
  computed: {
    ...mapState("processings", [
      "processingMethods", "processingTypes", "loading", "typesLoading"
    ]),
    ...mapState("units", ["units"])
  },
  watch: {
    value(v) {
      if (!v) {
        this.adding = false;
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("units", ["getUnits"]),
    ...mapActions("processings", [
      "getProcessingMethods", "addProcessingMethod", "deleteProcessingMethod", 
      "updateProcessingMethod", "getProcessingTypes", "toggleAllSelected"
    ]),
    ...mapMutations("processings", [
      "selectAll", "deselectAll", "toggleSelected"
    ]),
    initialize() {
      if (this.processingMethods.length == 0) {
        this.getProcessingMethods();
      }
      if (this.getProcessingTypes.length == 0) {
        this.getProcessingTypes();
      }
      if (this.units.length == 0) {
        this.getUnits();
      }
    },
    add() {
      this.addProcessingMethod(this.processing).then(() => {
        this.adding = false;
        this.clearData();
      });
    },
    update(processing) {
      let data = {};

      Object.keys(this.processing).forEach(field => {
        data[field] = processing[field]
      });

      this.updateProcessingMethod(
        { processingMethodId: processing.id, data: data }
      ).then(() => {
        this.editingId = null;
      })
    },
    launchConfirmDelete(id) {
      this.deleteConfirmedCb = () => {
        this.deleteProcessingMethod(id);
        this.confirmDelete.state = false;
      }
      this.confirmDelete.message = 'Are you sure you want to delete this processing method?';
      this.confirmDelete.state = true;
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.table tfoot td .centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.modal-content {
  width: 1000px;
}

.modal-card {
  border-radius: 6px;
  width: 1000px;
}
</style>
